import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  ErrorMessage,
  Flex,
  LoadingIndicator,
  ModalV2,
  Text,
  toast,
} from "@jsq/ds/core";
import { useGetDownloadReportStatusLazyQuery } from "~/graphql/supergraphHooks";
import {
  getGeneratingModalDescriptionText,
  getGeneratingModalToastText,
} from "../ExportActions/utils";
import {
  KeyOfDocumentExportAction,
  KeyOfExportActions,
  KeyOfPositionExportActions,
} from "../ExportActions/utils/constants";

export type GeneratingModalProps = {
  showGeneratingModal: boolean;
  closeGeneratingModal: () => void;
  jobId: number;
  reportType: KeyOfExportActions | KeyOfDocumentExportAction | KeyOfPositionExportActions;
};

export const GeneratingModal = ({
  showGeneratingModal,
  closeGeneratingModal,
  jobId,
  reportType,
}: GeneratingModalProps) => {
  const [jobInProgress, setJobInProgress] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [getDownloadReportStatus] = useGetDownloadReportStatusLazyQuery({
    variables: { reportId: jobId },

    fetchPolicy: "network-only",
  });
  const timer = useRef(-1);
  const setReportGenerationStatusTimer = () => {
    timer.current = window.setTimeout(() => {
      // We show the "generating modal" as soon as the user clicks the report to be generated so that they get immediate ux feedback.
      // If the mutation api response is not received before 1s, jobId will be -1 here, so make the api call after validating that we have the right the jobId - RKT-2587.
      // The case where mutation api itself fails is handled in ExportActionsComponent.
      if (jobId > -1) {
        fetchReportGenerationStatus().catch(() => {
          setErrorMessage("Error downloading report. Please contact support.");
        });
      } else {
        setReportGenerationStatusTimer();
      }
    }, 1000);
  };
  const fetchReportGenerationStatus = async () => {
    try {
      const { data } = await getDownloadReportStatus();
      if (!data?.getComplianceReport) {
        setErrorMessage("Error downloading report. Please contact support.");
      }
      switch (data?.getComplianceReport?.status) {
        case "ready":
          setJobInProgress(false);
          if (data.getComplianceReport.s3Url) {
            toast.info(`${getGeneratingModalToastText(reportType)} successfully exported`, {
              position: "bottom-center",
            });
            window.open(data.getComplianceReport.s3Url, "_blank");
            closeGeneratingModal();
          } else {
            setErrorMessage("Error downloading report. Please contact support.");
          }
          break;
        case "pending":
          setReportGenerationStatusTimer();
          break;
        case "error":
          setJobInProgress(false);
          setErrorMessage("Error downloading report. Please contact support.");
      }
    } catch {
      setErrorMessage("Error downloading report. Please contact support.");
    }
  };
  const onOkHandler = () => {
    closeGeneratingModal();
  };
  useEffect(() => {
    setReportGenerationStatusTimer();
    return () => {
      clearTimeout(timer.current);
    };
  }, [jobId]);

  return (
    <ModalV2
      show={showGeneratingModal}
      onHide={closeGeneratingModal}
      data-testid="generating-export-data-modal"
      closeOnClickOutside={false}
      backdrop={true}
      title="Generating report"
    >
      <ModalV2.Body>
        <Text align="left" marginBottom="md">
          Generating {getGeneratingModalDescriptionText(reportType)} report. Your report will
          download automatically when complete.
        </Text>
        {jobInProgress && <LoadingIndicator variant="primary" />}
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </ModalV2.Body>
      {errorMessage && (
        <ModalV2.Footer>
          <Flex css={{ flexDirection: "row", justifyContent: "flex-end" }}>
            <Button
              onClick={onOkHandler}
              data-cy="generating-export-data-modal-ok"
              data-testid="generating-export-data-modal-ok"
            >
              Close
            </Button>
          </Flex>
        </ModalV2.Footer>
      )}
    </ModalV2>
  );
};
